import React, { useState } from 'react';
import axios from 'axios';
import { XCircle, Plus, Trash2, Calendar, FileText, AlertCircle } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const AmendmentForm = ({ lease, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    effective_date: new Date().toISOString().split('T')[0],
    amendment_type: 'amendment',
    description: '',
    changes: {},
    document_url: '',
    document_filename: ''
  });
  
  const [changeKey, setChangeKey] = useState('');
  const [changeValue, setChangeValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleAddChange = () => {
    if (changeKey && changeValue) {
      setFormData(prev => ({
        ...prev,
        changes: {
          ...prev.changes,
          [changeKey]: isNaN(changeValue) ? changeValue : parseFloat(changeValue)
        }
      }));
      setChangeKey('');
      setChangeValue('');
    }
  };

  const handleRemoveChange = (key) => {
    const newChanges = { ...formData.changes };
    delete newChanges[key];
    setFormData(prev => ({ ...prev, changes: newChanges }));
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.effective_date) {
      setError('Title and effective date are required');
      return;
    }

    setSaving(true);
    setError(null);
    
    try {
      await axios.post(
        `${API_BASE}/amendments/`,
        {
          lease_id: lease.id,
          title: formData.title,
          effective_date: formData.effective_date,
          amendment_type: formData.amendment_type,
          description: formData.description,
          changes: Object.keys(formData.changes).length > 0 ? formData.changes : null,
          document_url: formData.document_url || null,
          document_filename: formData.document_filename || null
        },
        { withCredentials: true }
      );
      
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error creating amendment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
      <div className="bg-gray-800 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        
        <div className="p-5 border-b border-gray-700 flex justify-between items-center sticky top-0 bg-gray-800">
          <div>
            <h3 className="text-white font-bold text-lg">Create Amendment</h3>
            <p className="text-gray-400 text-sm">
              Lease {lease.lease_number} — {lease.hangar_number}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <XCircle size={24} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Type selection */}
          <div>
            <label className="text-gray-400 text-sm block mb-1">Type *</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setFormData(f => ({ ...f, amendment_type: 'amendment' }))}
                className={`p-3 rounded-lg border-2 transition ${
                  formData.amendment_type === 'amendment'
                    ? 'border-yellow-500 bg-yellow-500/10 text-yellow-400'
                    : 'border-gray-600 text-gray-400'
                }`}
              >
                <div className="font-medium">Amendment</div>
                <div className="text-xs">Modifies existing clauses</div>
              </button>
              <button
                type="button"
                onClick={() => setFormData(f => ({ ...f, amendment_type: 'addendum' }))}
                className={`p-3 rounded-lg border-2 transition ${
                  formData.amendment_type === 'addendum'
                    ? 'border-purple-500 bg-purple-500/10 text-purple-400'
                    : 'border-gray-600 text-gray-400'
                }`}
              >
                <div className="font-medium">Addendum</div>
                <div className="text-xs">Adds new clauses</div>
              </button>
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="text-gray-400 text-sm block mb-1">Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(f => ({ ...f, title: e.target.value }))}
              placeholder="e.g., Rent Increase - June 2026"
              className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white"
            />
          </div>

          {/* Effective Date */}
          <div>
            <label className="text-gray-400 text-sm block mb-1 flex items-center gap-1">
              <Calendar size={14} /> Effective Date *
            </label>
            <input
              type="date"
              value={formData.effective_date}
              onChange={(e) => setFormData(f => ({ ...f, effective_date: e.target.value }))}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white"
            />
          </div>

          {/* Description */}
          <div>
            <label className="text-gray-400 text-sm block mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(f => ({ ...f, description: e.target.value }))}
              rows={3}
              placeholder="Describe the purpose of this amendment..."
              className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm"
            />
          </div>

          {/* Changes (JSON key-value pairs) */}
          <div>
            <label className="text-gray-400 text-sm block mb-1">Changes (optional)</label>
            <div className="bg-gray-700/50 rounded-lg p-3">
              {/* Existing changes */}
              {Object.entries(formData.changes).map(([key, value]) => (
                <div key={key} className="flex justify-between items-center mb-2 p-2 bg-gray-800 rounded">
                  <div>
                    <span className="text-white text-sm font-mono">{key}</span>
                    <span className="text-gray-400 text-sm ml-2">→</span>
                    <span className="text-green-400 text-sm ml-2">{String(value)}</span>
                  </div>
                  <button
                    onClick={() => handleRemoveChange(key)}
                    className="text-red-400 hover:text-red-300"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}

              {/* Add new change */}
              <div className="flex gap-2 mt-2">
                <input
                  type="text"
                  placeholder="Field name (e.g., monthly_rent)"
                  value={changeKey}
                  onChange={(e) => setChangeKey(e.target.value)}
                  className="flex-1 bg-gray-800 border border-gray-600 rounded-lg p-2 text-white text-sm"
                />
                <input
                  type="text"
                  placeholder="Value"
                  value={changeValue}
                  onChange={(e) => setChangeValue(e.target.value)}
                  className="flex-1 bg-gray-800 border border-gray-600 rounded-lg p-2 text-white text-sm"
                />
                <button
                  onClick={handleAddChange}
                  className="px-3 bg-green-600 hover:bg-green-700 rounded-lg text-white"
                >
                  <Plus size={16} />
                </button>
              </div>
              <p className="text-gray-500 text-xs mt-2">
                Example: monthly_rent → 550, end_date → 2027-12-31
              </p>
            </div>
          </div>

          {/* Document URL */}
          <div>
            <label className="text-gray-400 text-sm block mb-1">Document URL (optional)</label>
            <input
              type="text"
              value={formData.document_url}
              onChange={(e) => setFormData(f => ({ ...f, document_url: e.target.value }))}
              placeholder="/uploads/amendments/amendment-001.pdf"
              className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm"
            />
            <input
              type="text"
              value={formData.document_filename}
              onChange={(e) => setFormData(f => ({ ...f, document_filename: e.target.value }))}
              placeholder="Filename (optional)"
              className="w-full mt-2 bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm"
            />
          </div>

          {/* Error message */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle size={16} className="text-red-400 mt-0.5" />
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}
        </div>

        <div className="p-5 border-t border-gray-700 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || !formData.title || !formData.effective_date}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg text-white flex items-center gap-2"
          >
            <FileText size={16} />
            {saving ? 'Creating...' : 'Create Amendment'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AmendmentForm;